import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createSelector } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import BuyerCard, { BuyerCardConverter } from 'routes/profile/models/BuyerCard';
import { manualDeleteAuthorizedApplication } from 'routes/profile/account/AuthorizedApplications/authorizedAppsSlice';
import {
  GiftCard,
  GiftCardDetail,
  Money,
  PAGE_SIZE,
} from 'routes/profile/models/GiftCard';
import {
  BuyerState,
  addIdentifier,
  fetchBuyer,
  manualDeleteAddress,
  manualDeleteProfile,
  manualDeleteVerifiedIdentifier,
  manualUpdateBuyer,
  selectAddresses,
  selectIdentifierCollection,
  selectPrimaryAddressId,
  selectPrimaryId,
  updatePrimaryPaymentCardId,
} from 'store/buyerSlice';
import {
  Name,
  Profile,
  ProfileConverters,
} from 'routes/profile/models/Profile';
import { Identifier, IdentifierType } from 'routes/profile/models/Identifier';
import client from 'rpc/client';
import {
  AddProfileIdentifierRequest,
  AddProfileIdentifierResponse,
  VerifyAddProfileIdentifierRequest,
  VerifyAddProfileIdentifierResponse,
  DeleteProfileIdentifierRequest,
  DeleteProfileIdentifierResponse,
  VerifyEmailByTokenRequest,
  VerifyEmailByTokenResponse,
} from 'rpc/model/squareup/buyerportal/profile/verified';
import {
  RemoveAddressRequest,
  RemoveAddressResponse,
  UpdateBuyerRequest,
  UpdateBuyerResponse,
} from 'rpc/model/squareup/buyerportal/request/request';
import { Money as RpcMoney } from 'rpc/model/squareup/common/currency';
import { RequestStatus } from 'rpc/model/squareup/customers/request';
import {
  AuthorizedApplicationName,
  UnlinkAuthorizedApplicationRequest,
  UnlinkAuthorizedApplicationResponse,
} from 'rpc/model/squareup/buyerportal/authorizedapps/data';
import {
  ExportProfileRequest,
  ExportProfileResponse,
  DeleteProfileResponse,
} from 'rpc/model/squareup/buyerportal/profile/privacy';
import {
  CreateCardResponse,
  DeleteCardResponse,
  ListCardsRequest,
  ListCardsResponse,
  DeleteCardRequest,
  CreateCardRequest,
} from 'rpc/model/squareup/buyerportal/cards/data';
import {
  ListGiftCardsResponse,
  ListGiftCardsRequest,
  RetrieveGiftCardDetailResponse,
  RetrieveGiftCardDetailRequest,
  UnlinkBuyerFromGiftCardResponse,
  UnlinkBuyerFromGiftCardRequest,
  TransferGiftCardBalanceResponse,
  TransferGiftCardBalanceRequest,
  LinkBuyerToGiftCardResponse,
  RetrieveGiftCardDetailBySecretResponse,
  LinkBuyerToGiftCardRequest,
  RetrieveGiftCardDetailBySecretRequest,
} from 'rpc/model/squareup/buyerportal/giftcards/data';
import {
  RetrieveSuggestedProfileIdentifiersRequest,
  RetrieveSuggestedProfileIdentifiersResponse,
  UnlinkSuggestedProfileIdentifierRequest,
  UnlinkSuggestedProfileIdentifierResponse,
} from 'rpc/model/squareup/buyerportal/profile/suggested_identifier';
import { CurrencyCode } from 'rpc/model/squareup/common/currency_codes';
import { AppState } from 'store';
import { getRpcIdentifierType } from 'utils/identifiers';
import { callApi, delay, getResponseError } from './utils';

export const buyerportalApi = createApi({
  reducerPath: 'buyerportalApi',
  baseQuery: fetchBaseQuery({ baseUrl: '' }), // unused
  tagTypes: [
    'GiftCardList',
    'GiftCardDetails',
    'retrieveMerchantEmailPreferences',
    'retrieveGlobalEmailPreferences',
    'CardPreferences',
  ],
  endpoints: (builder) => ({
    verifyEmailByToken: builder.mutation<string, string>({
      async queryFn(token) {
        const response = await callApi<VerifyEmailByTokenResponse>(
          async () =>
            await client.verifyEmailByToken(
              VerifyEmailByTokenRequest.create({
                token,
              })
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        return {
          data: response.data!.obfuscatedVerifiedEmail,
        };
      },
    }),
    updateName: builder.mutation<Name, Name>({
      async queryFn(name, api) {
        const { profile } = api.getState() as AppState;
        const profileCopy: BuyerState = {
          ...profile,
          firstName: name?.firstName,
          lastName: name?.lastName,
        };

        const updateBuyerRequest = new UpdateBuyerRequest.Builder()
          .buyer(ProfileConverters.toRpcBuyer(profileCopy))
          .idempotencyKey(uuidv4())
          .build();

        const response = await callApi<UpdateBuyerResponse>(
          async () => await client.updateBuyer(updateBuyerRequest)
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        api.dispatch(
          manualUpdateBuyer(
            ProfileConverters.fromRpcBuyer(response.data!.buyer!)
          )
        );

        return {
          data: name,
        };
      },
    }),
    updatePrimaryIdentifierId: builder.mutation<
      Profile,
      { id: string; identifierType: IdentifierType }
    >({
      async queryFn(updateRequest, api) {
        const profile = (api.getState() as AppState).profile as Profile;
        const updatedBuyer: Profile = { ...profile };

        switch (updateRequest.identifierType) {
          case IdentifierType.Email: {
            updatedBuyer.primaryEmailId = updateRequest.id;
            break;
          }
          case IdentifierType.Phone: {
            updatedBuyer.primaryPhoneId = updateRequest.id;
            break;
          }
          case IdentifierType.Card: {
            updatedBuyer.primaryCardId = updateRequest.id;
            break;
          }
          default: {
            return {
              data: profile,
            };
          }
        }

        const updateBuyerRequest = new UpdateBuyerRequest.Builder()
          .buyer(ProfileConverters.toRpcBuyer(updatedBuyer))
          .idempotencyKey(uuidv4())
          .build();

        const response = await callApi<UpdateBuyerResponse>(
          async () => await client.updateBuyer(updateBuyerRequest)
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        api.dispatch(
          manualUpdateBuyer(
            ProfileConverters.fromRpcBuyer(response.data!.buyer!)
          )
        );

        return {
          data: ProfileConverters.fromRpcBuyer(response.data!.buyer!),
        };
      },
    }),
    requestAddProfileIdentifier: builder.mutation<
      null,
      { identifierType: IdentifierType; identifierValue: string }
    >({
      async queryFn(request) {
        const addProfileIdentifierRequest = AddProfileIdentifierRequest.create({
          idempotencyKey: uuidv4(),
          identifierType: getRpcIdentifierType(request.identifierType),
          identifierRawValue: request.identifierValue,
        });

        const response = await callApi<AddProfileIdentifierResponse>(
          async () =>
            await client.requestAddProfileIdentifier(
              addProfileIdentifierRequest
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        return {
          data: null,
        };
      },
    }),
    verifyAddProfileIdentifier: builder.mutation<
      Identifier,
      {
        identifierType: IdentifierType;
        identifierValue: string;
        verificationCode: string;
      }
    >({
      async queryFn(request, api) {
        const verifyAddProfileIdentifierRequest =
          VerifyAddProfileIdentifierRequest.create({
            identifierRawValue: request.identifierValue,
            identifierType: getRpcIdentifierType(request.identifierType),
            verificationCode: request.verificationCode,
          });

        const response = await callApi<VerifyAddProfileIdentifierResponse>(
          async () =>
            await client.verifyAddProfileIdentifier(
              verifyAddProfileIdentifierRequest
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          if (!response.data!.errors || response.data!.errors.length === 0) {
            return getResponseError(response.data!);
          }

          const serverError = response.data!.errors![0];

          if (
            serverError.field === 'FIELD_VERIFICATION_CODE' ||
            (serverError.field === 'FIELD_PAN' &&
              serverError.code === 'CODE_INVALID')
          ) {
            return {
              error: {
                data: 'Invalid verification code',
                status: RequestStatus.STATUS_BAD_REQUEST,
              },
            };
          } else if (
            serverError.field === 'FIELD_IDENTIFIER' &&
            serverError.code === 'CODE_PRECONDITION_FAILED'
          ) {
            return {
              error: {
                data: 'Identifier already exists on another profile',
                status: RequestStatus.STATUS_CONFLICT,
              },
            };
          }

          return getResponseError(response.data!);
        }

        const newIdentifier = Identifier.fromResponse(
          response.data!.identifier!
        );

        api.dispatch(addIdentifier(newIdentifier));

        return {
          data: newIdentifier,
        };
      },
      invalidatesTags: ['GiftCardList'], // Gift Cards are associated to buyer accounts via email
    }),
    deleteVerifiedIdentifier: builder.mutation<string, Identifier>({
      async queryFn(identifier, api) {
        const deleteIdentifierRequest = DeleteProfileIdentifierRequest.create({
          identifierId: identifier.token,
          identifierType: getRpcIdentifierType(identifier.identifierType),
        });

        const response = await callApi<DeleteProfileIdentifierResponse>(
          async () =>
            await client.deleteProfileIdentifier(deleteIdentifierRequest)
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        const primaryId = selectPrimaryId(
          api.getState() as AppState,
          identifier.identifierType
        ) as string;
        const identifiers = selectIdentifierCollection(
          api.getState() as AppState,
          identifier.identifierType
        );

        // If the deleted identifier is the primary, we must fetch the buyer again to get the
        // new primary. We don't need to refetch if the identifier we delete is the last of its type.
        if (primaryId === identifier.token && identifiers.length > 1) {
          api.dispatch(fetchBuyer({ skipLoadingState: true }));
        }

        api.dispatch(manualDeleteVerifiedIdentifier(identifier));

        return {
          data: identifier.token,
        };
      },
      invalidatesTags: ['GiftCardList', 'CardPreferences'], // Gift Cards are associated to buyer accounts via email
    }),
    updatePrimaryAddressId: builder.mutation<Profile, string>({
      async queryFn(id, api) {
        const profile = (api.getState() as AppState).profile as Profile;
        const updatedBuyer: Profile = { ...profile };

        updatedBuyer.primaryAddressId = id;

        const updateBuyerRequest = new UpdateBuyerRequest.Builder()
          .buyer(ProfileConverters.toRpcBuyer(updatedBuyer))
          .idempotencyKey(uuidv4())
          .build();

        const response = await callApi<UpdateBuyerResponse>(
          async () => await client.updateBuyer(updateBuyerRequest)
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        api.dispatch(
          manualUpdateBuyer(
            ProfileConverters.fromRpcBuyer(response.data!.buyer!)
          )
        );

        return {
          data: ProfileConverters.fromRpcBuyer(response.data!.buyer!),
        };
      },
    }),
    deleteAddress: builder.mutation<null, string>({
      async queryFn(id, api) {
        const removeAddressRequest = RemoveAddressRequest.create({
          addressToken: id,
        });

        const response = await callApi<RemoveAddressResponse>(
          async () => await client.removeAddress(removeAddressRequest)
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        const primaryId = selectPrimaryAddressId(api.getState() as AppState);
        const addresses = selectAddresses(api.getState() as AppState);

        // If the deleted address is the primary, we must fetch the buyer again to get the
        // new primary. We don't need to refetch if the address we delete is the last address.
        if (primaryId === id && addresses.length > 1) {
          api.dispatch(fetchBuyer());
        }

        api.dispatch(manualDeleteAddress(id));

        return {
          data: null,
        };
      },
    }),
    unlinkAuthorizedApplication: builder.mutation<
      null,
      AuthorizedApplicationName
    >({
      async queryFn(name, api) {
        const response = await callApi<UnlinkAuthorizedApplicationResponse>(
          async () =>
            await client.unlinkAuthorizedApplication(
              UnlinkAuthorizedApplicationRequest.create({ name })
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        api.dispatch(manualDeleteAuthorizedApplication(name));

        return {
          data: null,
        };
      },
    }),
    exportProfile: builder.mutation<
      null,
      { destinationEmail: string; idempotencyKey: string }
    >({
      async queryFn(request) {
        const response = await callApi<ExportProfileResponse>(
          async () =>
            await client.exportProfile(
              ExportProfileRequest.create({
                requestorEmail: request.destinationEmail,
                idempotencyKey: request.idempotencyKey,
              })
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        return {
          data: null,
        };
      },
    }),
    deleteProfile: builder.mutation<null, void>({
      async queryFn(_, api) {
        const response = await callApi<DeleteProfileResponse>(
          async () =>
            await client.deleteProfile({
              idempotencyKey: uuidv4(),
            })
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        api.dispatch(manualDeleteProfile());

        return {
          data: null,
        };
      },
    }),
    retrieveSuggestedProfileIdentifiers: builder.query<Identifier[], void>({
      async queryFn() {
        const response =
          await callApi<RetrieveSuggestedProfileIdentifiersResponse>(
            async () =>
              await client.retrieveSuggestedProfileIdentifiers(
                RetrieveSuggestedProfileIdentifiersRequest.create({})
              )
          );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        return {
          data: response.data!.identifiers!.map(Identifier.fromResponse),
        };
      },
    }),
    unlinkSuggestedProfileIdentifier: builder.mutation<null, Identifier>({
      async queryFn(identifier) {
        const response =
          await callApi<UnlinkSuggestedProfileIdentifierResponse>(
            async () =>
              await client.unlinkSuggestedProfileIdentifier(
                UnlinkSuggestedProfileIdentifierRequest.create({
                  identifierId: identifier.token,
                  identifierType: getRpcIdentifierType(
                    identifier.identifierType
                  ),
                })
              )
          );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        return {
          data: null,
        };
      },
      async onQueryStarted(removedIdentifier, { dispatch, queryFulfilled }) {
        const updateResult = dispatch(
          buyerportalApi.util.updateQueryData(
            'retrieveSuggestedProfileIdentifiers',
            undefined,
            (identifiers) => {
              return identifiers.filter(
                (identifier) => removedIdentifier.token !== identifier.token
              );
            }
          )
        );

        queryFulfilled.catch(updateResult.undo);
      },
    }),
    listCards: builder.query<BuyerCard[], void>({
      async queryFn() {
        const response = await callApi<ListCardsResponse>(
          async () => await client.listCards(ListCardsRequest.create())
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        return {
          data: BuyerCardConverter.fromRpcBuyerCards(response.data!.cards),
        };
      },
    }),
    listGiftCards: builder.query<GiftCard[], number>({
      providesTags: ['GiftCardList'],
      async queryFn(limit) {
        const response = await callApi<ListGiftCardsResponse>(
          async () =>
            await client.listGiftCards(
              ListGiftCardsRequest.create({
                limit,
                offset: 0,
              })
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        return {
          data: response.data!.giftCards!.map(GiftCard.fromResponse),
        };
      },
    }),
    addGiftCard: builder.mutation<
      null,
      { giftCardDetail: GiftCardDetail; giftCardTokenSecret: string }
    >({
      async queryFn({ giftCardTokenSecret }) {
        const response = await callApi<LinkBuyerToGiftCardResponse>(
          async () =>
            await client.linkBuyerToGiftCard(
              LinkBuyerToGiftCardRequest.create({
                giftCardTokenSecret,
              })
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          const serverError = response.data!.errors![0];

          if (serverError.code === 'CODE_CONFLICT') {
            return {
              error: {
                data: 'Already added gift card.',
                status: RequestStatus.STATUS_BAD_REQUEST,
              },
            };
          } else {
            return getResponseError(response.data!);
          }
        }

        return {
          data: null,
        };
      },
      async onQueryStarted({ giftCardDetail }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            buyerportalApi.util.updateQueryData(
              'listGiftCards',
              PAGE_SIZE,
              (giftCards) => {
                giftCards.push(GiftCardDetail.toGiftCard(giftCardDetail));
              }
            )
          );
        } catch {
          // no-op
        }
      },
      invalidatesTags: [{ type: 'GiftCardDetails' }],
    }),
    removeGiftCard: builder.mutation<null, string>({
      async queryFn(giftCardToken) {
        const response = await callApi<UnlinkBuyerFromGiftCardResponse>(
          async () =>
            await client.unlinkBuyerFromGiftCard(
              UnlinkBuyerFromGiftCardRequest.create({
                giftCardToken,
              })
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        return {
          data: null,
        };
      },
      async onQueryStarted(removedGiftCardToken, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            buyerportalApi.util.updateQueryData(
              'listGiftCards',
              PAGE_SIZE,
              (cards) => {
                return cards.filter(
                  (card) => card.token !== removedGiftCardToken
                );
              }
            )
          );
        } catch {
          // no-op
        }
      },
    }),
    retrieveGiftCardDetail: builder.query<
      GiftCardDetail,
      { giftCardToken: string; merchantUnitToken: string }
    >({
      providesTags: ['GiftCardDetails'],
      async queryFn({ giftCardToken, merchantUnitToken }) {
        const response = await callApi<RetrieveGiftCardDetailResponse>(
          async () =>
            await client.retrieveGiftCardDetail(
              RetrieveGiftCardDetailRequest.create({
                giftCardTokenFingerprint: giftCardToken,
                unitTokenFingerprint: merchantUnitToken,
              })
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        return {
          data: GiftCardDetail.fromResponse(response.data!.giftCardDetail!),
        };
      },
    }),
    batchRetrieveGiftCardDetail: builder.query<
      GiftCardDetail[],
      { giftCardTokens: string[]; merchantUnitToken: string }
    >({
      providesTags: ['GiftCardDetails'],
      async queryFn({ giftCardTokens, merchantUnitToken }) {
        const giftCardDetails: GiftCardDetail[] = [];
        const batches = [];
        const DELAY_MS = 1000;
        const BATCH_SIZE = 5;

        for (let i = 0; i < giftCardTokens.length; i += BATCH_SIZE) {
          batches.push(giftCardTokens.slice(i, i + BATCH_SIZE));
        }

        /**
         * We need to throttle the number of calls to `RetrieveGiftCardDetail`.
         * Without throttling, batch retrieving gift card details will spawn X number of requests,
         * where X is the number of gift cards the buyer has attached to their account.
         *
         * If we spawn X requests in rapid succession, we may violate rate limiting, resulting in failed calls.
         */
        for (const batch of batches) {
          const reqs = batch.map((giftCardToken) => {
            return callApi<RetrieveGiftCardDetailResponse>(() =>
              client.retrieveGiftCardDetail(
                RetrieveGiftCardDetailRequest.create({
                  giftCardTokenFingerprint: giftCardToken,
                  unitTokenFingerprint: merchantUnitToken,
                })
              )
            );
          });

          const responses = await Promise.all(reqs);

          for (const response of responses) {
            if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
              return getResponseError(response.data!);
            }
            giftCardDetails.push(
              GiftCardDetail.fromResponse(response.data!.giftCardDetail!)
            );
          }

          // Delay before processing the next batch
          await delay(DELAY_MS);
        }

        return {
          data: giftCardDetails,
        };
      },
    }),
    retrieveGiftCardDetailBySecret: builder.query<GiftCardDetail, string>({
      async queryFn(giftCardTokenSecret) {
        const response = await callApi<RetrieveGiftCardDetailBySecretResponse>(
          async () =>
            await client.retrieveGiftCardDetailBySecret(
              RetrieveGiftCardDetailBySecretRequest.create({
                giftCardTokenSecret,
              })
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        return {
          data: GiftCardDetail.fromResponse(response.data!.giftCardDetail!),
        };
      },
    }),
    transferGiftCardBalance: builder.mutation<
      null,
      {
        fromGiftCardToken: string;
        toGiftCardToken: string;
        amount: Money;
      }
    >({
      async queryFn({ fromGiftCardToken, toGiftCardToken, amount }) {
        const response = await callApi<TransferGiftCardBalanceResponse>(
          async () =>
            await client.transferGiftCardBalance(
              TransferGiftCardBalanceRequest.create({
                fromGiftCardToken,
                toGiftCardToken,
                amount: RpcMoney.create({
                  amount: amount.amount,
                  currencyCode:
                    CurrencyCode[
                      amount.currencyCode as keyof typeof CurrencyCode
                    ],
                }),
                idempotencyKey: uuidv4(),
              })
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          if (!response.data!.errors) {
            return getResponseError(response.data!);
          }

          const serverError = response.data!.errors![0];

          if (serverError.field === 'FIELD_GIFT_CARD_TRANSFER_AMOUNT') {
            return {
              error: {
                data: 'Gift card transfer amount is invalid',
                status: RequestStatus.STATUS_BAD_REQUEST,
              },
            };
          }

          return getResponseError(response.data!);
        }

        return {
          data: null,
        };
      },
      async onQueryStarted(
        { fromGiftCardToken, toGiftCardToken, amount },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;

          dispatch(
            buyerportalApi.util.updateQueryData(
              'listGiftCards',
              PAGE_SIZE,
              (giftCards) => {
                const fromGiftCard = giftCards.find(
                  (gc) => gc.token === fromGiftCardToken
                );

                const toGiftCard = giftCards.find(
                  (gc) => gc.token === toGiftCardToken
                );

                fromGiftCard!.balance.amount -= amount.amount;
                toGiftCard!.balance.amount += amount.amount;
              }
            )
          );
        } catch {
          // no-op
        }
      },
      invalidatesTags: [{ type: 'GiftCardDetails' }],
    }),
    deleteCard: builder.mutation<null, string>({
      async queryFn(cardId, api) {
        const response = await callApi<DeleteCardResponse>(
          async () =>
            await client.deleteCard(
              DeleteCardRequest.create({
                cardId,
              })
            )
        );

        if (response.data!.status !== RequestStatus.STATUS_SUCCESS) {
          return getResponseError(response.data!);
        }

        const cards = selectCards(api.getState() as AppState);

        if (cards.length >= 2) {
          // If more than 1 card, a new primary will be chosen according to the same rules as the backend:
          // Reverse-alpha sort and pick the first one in the resulting sorted collection. (CRM-12706)
          const alphaSortedRemainingChargeableCards = cards
            .filter((cc) => cc.cardsApiId !== cardId) // exclude card we're about to delete from consideration
            .sort((a, b) => a.cardsApiId.localeCompare(b.cardsApiId) * -1); // Sort like the backend

          api.dispatch(
            updatePrimaryPaymentCardId(
              alphaSortedRemainingChargeableCards[0].cardsApiId
            )
          );
        } else {
          // If this was the last card, reset the primary id.
          api.dispatch(updatePrimaryPaymentCardId(''));
        }

        return {
          data: null,
        };
      },
      async onQueryStarted(removedCardId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            buyerportalApi.util.updateQueryData(
              'listCards',
              undefined,
              (cards) => {
                return cards.filter(
                  (card) => card.cardsApiId !== removedCardId
                );
              }
            )
          );
        } catch {
          // no-op
        }
      },
    }),
    createCard: builder.mutation<
      BuyerCard,
      { nonce: string; cardholderName?: string }
    >({
      async queryFn({ nonce, cardholderName }, api) {
        const requestBuilder = new CreateCardRequest.Builder()
          .cardNonce(nonce)
          .idempotencyKey(uuidv4());

        if (cardholderName) {
          requestBuilder.cardholderName(cardholderName);
        }

        const response = await callApi<CreateCardResponse>(
          async () => await client.createCard(requestBuilder.build())
        );

        if (
          response.data!.status !== RequestStatus.STATUS_SUCCESS ||
          !response.data?.card
        ) {
          return getResponseError(response.data!);
        }

        const cards = selectCards(api.getState() as AppState);

        if (cards.length === 0) {
          // user added first new card, make this primary
          api.dispatch(updatePrimaryPaymentCardId(response.data.card.cardId));
        }

        return {
          data: BuyerCardConverter.fromRpcBuyerCard(response.data!.card),
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: addedCard } = await queryFulfilled;
          dispatch(
            buyerportalApi.util.updateQueryData(
              'listCards',
              undefined,
              (cards) => {
                return [...cards, addedCard];
              }
            )
          );

          dispatch(
            buyerportalApi.util.updateQueryData(
              'retrieveSuggestedProfileIdentifiers',
              undefined,
              (unverifiedIdentifiers) => {
                return unverifiedIdentifiers.filter(
                  (i) => i.token !== addedCard.preferenceCardId
                );
              }
            )
          );
        } catch {
          // no-op
        }
      },
    }),
  }),
});

export const selectCards = createSelector(
  buyerportalApi.endpoints.listCards.select(),
  (listCardsResult) => listCardsResult?.data ?? []
);

export const selectUnverifiedIdentifierCollection = createSelector(
  (appState: AppState, _: IdentifierType) =>
    appState.profile.suggestedIdentifiers,
  (_: AppState, targetIdentifierType: IdentifierType) => targetIdentifierType,
  (suggestedIdentifiers: Identifier[], targetIdentifierType: IdentifierType) =>
    suggestedIdentifiers.filter(
      (identifier) => identifier.identifierType === targetIdentifierType
    )
);

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useVerifyEmailByTokenMutation,
  useUpdateNameMutation,
  useUpdatePrimaryIdentifierIdMutation,
  useRequestAddProfileIdentifierMutation,
  useVerifyAddProfileIdentifierMutation,
  useDeleteVerifiedIdentifierMutation,
  useDeleteProfileMutation,
  useUpdatePrimaryAddressIdMutation,
  useUnlinkAuthorizedApplicationMutation,
  useDeleteAddressMutation,
  useExportProfileMutation,
  useRetrieveSuggestedProfileIdentifiersQuery,
  useUnlinkSuggestedProfileIdentifierMutation,
  useListCardsQuery,
  useListGiftCardsQuery,
  useDeleteCardMutation,
  useAddGiftCardMutation,
  useRemoveGiftCardMutation,
  useTransferGiftCardBalanceMutation,
  useCreateCardMutation,
  // Lazy queries allow us to manually trigger the query instead of on component load
  useLazyRetrieveGiftCardDetailQuery,
  useLazyRetrieveGiftCardDetailBySecretQuery,
  useLazyBatchRetrieveGiftCardDetailQuery,
} = buyerportalApi;
